import React, { useContext, useEffect, useState } from 'react';
import {
  Container,
  Box,
  Tabs,
  Tab,
  Paper,
  AppBar,
  Toolbar,
  Typography,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Button
} from '@mui/material';
import { Settings as SettingsIcon, TableChart as TableIcon, ViewModule as AssortmentIcon, AutoFixHigh as AIIcon, Login as LoginIcon, Schema as ModelIcon, AccountTree as PivotIcon } from '@mui/icons-material';

// Import components
import UIConfigurationEditor from './components/UIConfig/UIConfigurationEditor';
import ViewManager from './components/ViewManager/ViewManager';
import ViewTemplateManager from './components/ViewTemplateManager/ViewTemplateManager';
import MassUpdate from './components/MassUpdate/MassUpdate';
import ModelManager from './components/ModelManager/ModelManager';
import PivotManagerTest from './components/PivotManagerTest/PivotManagerTest';
import UserMenu from './components/Auth/UserMenu';
import { AuthContext, AuthProvider, type IAuthContext, type TAuthConfig } from 'react-oauth2-code-pkce'
import { ApiProvider } from './config/axios';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
  },
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`app-tabpanel-${index}`}
      aria-labelledby={`app-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `app-tab-${index}`,
    'aria-controls': `app-tabpanel-${index}`,
  };
}

function AppContent() {
  const [tabValue, setTabValue] = useState(0);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const { tokenData, token, logIn, logOut, loginInProgress } = useContext(AuthContext);

  if (!token) {
    return (<ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'background.default'
      }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%', mx: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Unified Configuration Manager
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please sign in to access the application
            </Typography>
          </Box>

          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<LoginIcon />}
            onClick={() => logIn()}
            sx={{ mb: 2 }}
          >
            Log In
          </Button>
        </Paper>
      </Box>
    </ThemeProvider>)
  }

  // Show main application for authenticated users
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static" elevation={0}>
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Unified Configuration Manager
            </Typography>
            <UserMenu user={{ username: tokenData['email'] ?? tokenData['sub'] }} onLogout={logOut} />
          </Toolbar>
        </AppBar>

        <Container maxWidth={false} sx={{ mt: 2 }}>
          <Paper elevation={1} sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="configuration manager tabs"
                sx={{ px: 2 }}
              >
                <Tab
                  label="UI Manager"
                  icon={<AssortmentIcon />}
                  iconPosition="start"
                  {...a11yProps(0)}
                />
                <Tab
                  label="View Manager"
                  icon={<SettingsIcon />}
                  iconPosition="start"
                  {...a11yProps(1)}
                />
                <Tab
                  label="View Template Manager"
                  icon={<TableIcon />}
                  iconPosition="start"
                  {...a11yProps(2)}
                />
                <Tab
                  label="Model Manager"
                  icon={<ModelIcon />}
                  iconPosition="start"
                  {...a11yProps(3)}
                />
                <Tab
                  label="Pivot Manager"
                  icon={<PivotIcon />}
                  iconPosition="start"
                  {...a11yProps(4)}
                />
                <Tab
                  label="AI Assistant"
                  icon={<AIIcon />}
                  iconPosition="start"
                  {...a11yProps(5)}
                />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <UIConfigurationEditor />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <ViewManager />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <ViewTemplateManager />
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <ModelManager />
            </TabPanel>

            <TabPanel value={tabValue} index={4}>
              <PivotManagerTest />
            </TabPanel>

            <TabPanel value={tabValue} index={5}>
              <MassUpdate />
            </TabPanel>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

function App() {
  const [frontendConfig, setFrontendConfig] = useState<TAuthConfig | null>(null);
  useEffect(() => {
    const fetchConfig = async () => {
      while (true) {
        try {
          const response = await fetch('/api/frontend');
          const data = await response.json();
          const oidcRes = await fetch(`${data.issuer}/.well-known/openid-configuration`)
          const wellKnown = await oidcRes.json();
          setFrontendConfig({
            clientId: data.clientId,
            authorizationEndpoint: wellKnown.authorization_endpoint,
            tokenEndpoint: wellKnown.token_endpoint,
            redirectUri: window.location.origin,
            scope: 'openid profile email',
            autoLogin: false
          });
          break;
        } catch (e) {
          console.error("Failed to fetch frontend config, retrying ...", e);
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
    }
    fetchConfig();
  }, []);

  if (frontendConfig == null) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          <Typography variant="h6">Loading...</Typography>
        </Box>
      </ThemeProvider>
    );
  }
  return (
    <AuthProvider authConfig={frontendConfig}>
      <ApiProvider>
        <AppContent />
      </ApiProvider>
    </AuthProvider>
  );
}

export default App;